import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import SpreadSheet from '@/components/UI/SpreadSheet';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import HotTable from '@handsontable//react';
import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { MetadataField, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { PlannedAnimalDefinition } from './PlannedAnimals/PlannedAnimals.utils';
import {
  AnimalRow,
  generateInitialAnimals,
  handleMetadataChange,
  patchMetadata,
} from '@/components/Studies/Create/Steps/Animals/Animals.utils';

import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { metadataToColumnSettingMapper } from '@/components/CreateTreatmentGroups/CreateTreatmentGroups.utils.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import { getColumnData } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import { errorToast, preventNumberScroll } from '@/helpers.tsx';
import { FormProvider, useForm, useFormState, Validate } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import {
  AnimalCreateV1,
  AnimalDefinitionApiId,
  AnimalDefinitionUpsertV1,
  PlanTypeMap,
} from '@/model/PlannedAnimal.model.ts';
import { generateApiId } from '@/utils/ApiId.utils.ts';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema.tsx';
import { AnimalSexMap } from '@/model/Animal.model.ts';
import './PlannedAnimals.scss';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';

interface CreateAnimalsProps {
  plannedAnimals: Array<PlannedAnimalDefinition>;
  selectedIds: Set<AnimalDefinitionApiId>;
  groups: Array<TreatmentGroup>;
  species: Array<SpeciesV1>;
  studyMetadata: Array<MetadataField>;
  onSubmit: (animals: Array<AnimalDefinitionUpsertV1> | AnimalCreateV1) => Promise<void>;
  editing?: boolean;
}
interface AnimalsPerCageData {
  animalsPerCage: number | null;
}
const animalsPerCageValidation: Validate<AnimalsPerCageData['animalsPerCage'], AnimalsPerCageData> = (value) => {
  if (_isNil(value) || !Number.isFinite(value)) {
    return 'Must be a valid number';
  }
  if (value <= 0) {
    return 'Must be greater than 0';
  }
  if (value > 15) {
    return 'Maximum of 15 animals per cage';
  }
  return true;
};
const groupIdAccessor = (group: TreatmentGroup) => group.api_id as string;
const CreateAnimals: React.FC<CreateAnimalsProps> = ({
  plannedAnimals,
  selectedIds,
  groups,
  species,
  studyMetadata,
  onSubmit,
  editing = false,
}) => {
  const [tableValid, setTableValid] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedMetadata, setSelectedMetadata] = useState<Array<MetadataField>>([]);
  const [availableMetadata, setAvailableMetadata] = useState<Array<MetadataField>>([]);

  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const dobRequired = entityFieldRequiredMap?.dob ?? true;

  const ref = useRef<HotTable>(null);
  const dispatch = useDispatch();

  const formMethods = useForm<AnimalsPerCageData>({
    defaultValues: {
      animalsPerCage: 5,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const {
    register,
    formState: { errors },
  } = formMethods;
  const { isValid: cageValid } = useFormState({ control: formMethods.control, name: 'animalsPerCage' });

  const unmappedPlannedAnimals = useMemo(() => {
    return plannedAnimals.filter((animal) => _isNil(animal.animal_api_id));
  }, [plannedAnimals]);
  const selectedAnimals: Array<PlannedAnimalDefinition> = useMemo(() => {
    return (unmappedPlannedAnimals ?? []).filter((animal) => selectedIds.has(animal.api_id));
  }, [unmappedPlannedAnimals, selectedIds]);

  useEffect(() => {
    if (_isNotEmpty(studyMetadata)) {
      const populatedMetadata =
        selectedAnimals?.reduce((acc, animal) => {
          animal.metadata.forEach((meta) => {
            const metadataField = studyMetadata.find((m) => m.glossary_api_id === meta.type);
            if (_notNil(metadataField) && !acc.some((m) => m.id === metadataField.id)) {
              acc.push(metadataField);
            }
          });
          return acc;
        }, [] as Array<MetadataField>) ?? [];
      setSelectedMetadata(populatedMetadata);
      const remainingMetadata = studyMetadata.filter((meta) => {
        return !populatedMetadata.some((existingMeta) => existingMeta.title === meta.title);
      });
      setAvailableMetadata(remainingMetadata);
    }
  }, [selectedAnimals, studyMetadata]);

  const animalData = useMemo<Array<AnimalRow>>(() => {
    if (_isNotEmpty(selectedAnimals) && _isNotEmpty(groups)) {
      return selectedAnimals.reduce<Array<AnimalRow>>((acc, animal) => {
        if (_notNil(animal.animal_api_id)) {
          return acc as Array<AnimalRow>;
        }

        const group = groups.find((g) => g.api_id === animal.study_group_api_id);
        const speciesItem = species?.find((s) => s.api_id === animal.species_api_id);
        const strainItem = speciesItem?.strains?.find((strain) => strain.api_id === animal.strain_api_id);

        const metadataObj: Record<string, MetadataFieldWithValue> = {};
        animal.metadata.forEach((meta) => {
          const metadataField = studyMetadata.find((m) => m.glossary_api_id === meta.type);
          if (_notNil(metadataField)) {
            metadataObj[metadataField.id] = {
              id: metadataField.id,
              title: metadataField.title,
              value: meta.value,
              glossary_id: metadataField.glossary_id,
              glossary_api_id: metadataField.glossary_api_id,
              field_type: metadataField.field_type,
              read_only: metadataField.read_only,
              slug: metadataField.slug,
              required: metadataField.required,
              created_at: metadataField.created_at,
              updated_at: metadataField.updated_at,
            };
          }
        });

        acc.push({
          plannedId: animal.planned_id,
          apiId: animal.api_id,
          groupIdentifier: group?.api_id,
          sex: AnimalSexMap[animal.sex],
          species: speciesItem?.name ?? '',
          strain: strainItem?.name ?? '',
          metadata: metadataObj,
          isMapped: _notNil(animal.animal_api_id),
        } as AnimalRow);

        return acc;
      }, [] as Array<AnimalRow>);
    }
    if (_isEmpty(selectedAnimals) && _isNotEmpty(groups) && !editing) {
      return generateInitialAnimals(groups, dobRequired);
    }
    return [];
  }, [selectedAnimals, groups, species, studyMetadata, dobRequired, editing]);

  const unselectedPlannedAnimals = useMemo(() => {
    return plannedAnimals.filter((a) => !selectedIds.has(a.api_id));
  }, [plannedAnimals, selectedIds]);

  const columnDataForTable = useMemo(() => {
    return getColumnData(
      species,
      selectedMetadata,
      groups,
      groupIdAccessor,
      ref.current?.hotInstance ?? null,
      editing ? PlanTypeMap.planned : PlanTypeMap.registered,
      editing,
      dobRequired,
      editing ? unselectedPlannedAnimals : undefined
    );
  }, [species, selectedMetadata, groups, editing, dobRequired, unselectedPlannedAnimals]);

  useEffect(() => {
    const hotInstance = ref.current?.hotInstance;
    if (_notNil(hotInstance) && _isNotEmpty(animalData)) {
      window.requestAnimationFrame(() => hotInstance.validateCells((valid) => setTableValid(valid)));
    }
  }, [animalData, columnDataForTable]);

  const settings = useMemo<CustomGridSettings>(
    () => ({
      ...columnDataForTable,
      manualColumnResize: true,
      rowHeaders: true,
      outsideClickDeselects: true,
      allowInsertRow: true,
      allowInsertColumn: false,
      allowRemoveRow: false,
      minSpareRows: 0,
      columnSorting: false,
      className: 'htMiddle',
      rowHeights: 50,
      height: editing ? 'calc( 90vh - 250px)' : 'calc( 90vh - 300px)',
    }),
    [columnDataForTable, editing]
  );

  const resetColumns = () => {
    settings.columns = columnDataForTable.columns;
    settings.colHeaders = columnDataForTable.colHeaders;
  };

  const closeModal = () => {
    resetColumns();
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const handleDataChange = useCallback(() => {
    const hotInstance = ref.current?.hotInstance;
    hotInstance?.validateCells((valid) => setTableValid(valid));
  }, [setTableValid]);
  const formValid = cageValid && tableValid;
  const patchMetadataCallback = useCallback(() => {
    patchMetadata(ref.current?.hotInstance ?? null, settings, species);
  }, [species, settings]);

  const handleMetadataChangeCallback = useCallback(
    (metadata: Array<MetadataField>) => {
      handleMetadataChange(metadata, studyMetadata, setSelectedMetadata, setAvailableMetadata);
    },
    [studyMetadata, setSelectedMetadata, setAvailableMetadata]
  );

  const submitAnimals = async () => {
    if (formValid && !isSubmitting && _notNil(onSubmit)) {
      try {
        setIsSubmitting(true);
        // Read current data from HotTable instance (single source of truth)
        const hotInstance = ref.current?.hotInstance ?? null;
        const currentData = (hotInstance?.getSourceData() as Array<AnimalRow>) ?? [];

        const animalsToCreate = currentData
          .filter((animal) => !(animal.isMapped ?? false))
          .map((animal) => {
            const group = groups.find((g) => g.api_id === animal.groupIdentifier);
            const speciesItem = species.find((s) => s.name === animal.species);
            const strainItem = speciesItem?.strains?.find((strain) => strain.name === animal.strain);

            const metadataArray = Object.values(animal.metadata ?? {})
              .filter(
                (meta) =>
                  _notNil(meta.glossary_api_id) &&
                  _notNil(meta.value) &&
                  !_isEmptyString(meta.value) &&
                  _isNotEmpty(meta.value)
              )
              .map((meta) => ({
                type: meta.glossary_api_id!,
                value: meta.value,
              }));

            if (editing) {
              return {
                planned_id: animal.plannedId,
                api_id: animal?.apiId,
                study_group_api_id: group?.api_id,
                sex: animal.sex === 'Male' ? 'm' : animal.sex === 'Female' ? 'f' : animal.sex,
                species_api_id: speciesItem?.api_id,
                strain_api_id: strainItem?.api_id,
                metadata: metadataArray,
              };
            } else {
              return {
                api_id: animal?.apiId ?? generateApiId('adf'),
                overrides: {
                  name: animal.plannedId,
                  sex: animal.sex === 'Male' ? 'm' : animal.sex === 'Female' ? 'f' : animal.sex,
                  dob: animal.dob,
                  strain_api_id: strainItem?.api_id,
                  metadata: metadataArray,
                },
              };
            }
          });
        if (editing) {
          await onSubmit(animalsToCreate as Array<AnimalDefinitionUpsertV1>);
        } else {
          await onSubmit({
            cage_animal_count: formMethods.getValues('animalsPerCage'),
            definitions: animalsToCreate,
          } as AnimalCreateV1);
        }
        closeModal();
      } catch (err) {
        if (editing) {
          errorToast('Could not submit edit animals request');
        } else {
          errorToast('Could not submit create animals request');
        }
        ExceptionHandler.captureException(
          new InVivoError('Could not create animals', {
            cause: err,
            slug: 'create-animals-modal-error',
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleValidationErrorMessage = (isValid: boolean, value: ReactNode, row: number, prop: string | number) => {
    if (!isValid) {
      if (prop === 'groupIdentifier') {
        const hot = ref.current?.hotInstance;
        if (_notNil(hot) && !_isEmptyString(value)) {
          const group = groups.find((g) => g.api_id === value);
          if (editing) {
            const errorMsg =
              'Number of planned animals assigned exceeds maximum subjects allowed: ' + (group?.max_subjects ?? 0);
            hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
          } else {
            const availableCapacity = (group?.max_subjects ?? 0) - (group?.animals_count ?? 0);
            const errorMsg = 'Available group slots to register animals: ' + availableCapacity;
            hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
          }
        }
      }
    }
  };

  if (_isEmpty(selectedAnimals)) {
    return (
      <div
        style={{ minHeight: '90vh' }}
        data-test-component="EmptyPlannedAnimals"
        data-test-element="container"
        className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
      >
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No planned animals found</h3>
            <p className="mb3">
              {_isEmpty(groups)
                ? 'There are no planned animals available to create.'
                : 'Cannot edit mapped planned animals'}
            </p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ minHeight: '90vh' }}
      data-test-component="CreateAnimals"
      data-test-element="container"
      className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
    >
      <div className="flex flex-wrap self-start w-100" style={{ minHeight: 'calc( 90vh - 72px)' }}>
        <div className="createAnimalsHeaderSection">
          <h3 className="normal f4 lh-title">
            {editing ? 'Edit planned animals' : 'Create animals from planned animals'}
          </h3>
          <p className="lh-copy f5 pt2">Review and edit the planned animals data before registering animals.</p>
        </div>
        {_isEmpty(groups) ? (
          <div className="w-100 h-100">
            <Loading txt="Loading data..." />
          </div>
        ) : (
          <div
            className="ph4 pb3 w-100 ow-spreadsheet-styles"
            data-test-component="CreateAnimals"
            data-test-element="spreadsheet-container"
          >
            {!editing && (
              <FormProvider {...formMethods}>
                <div className="createAnimalsFormSection">
                  <form>
                    <label>Animals per Cage</label>
                    <input
                      {...register('animalsPerCage', {
                        required: 'Value required',
                        validate: animalsPerCageValidation,
                        valueAsNumber: true,
                      })}
                      type="number"
                      onWheel={preventNumberScroll}
                      style={{ marginBottom: 0, width: '150px' }}
                      className="mr2"
                      data-testid="generate-cage-count-input"
                    />
                    <ErrorMessage
                      errors={errors}
                      name={`animalsPerCage`}
                      render={({ message }) => <p className="f6 red pt1 w-90">{message}</p>}
                    />
                  </form>
                </div>
              </FormProvider>
            )}
            <SpreadSheet
              data={animalData}
              settings={settings}
              innerRef={ref}
              className="createAnimals"
              buttonClasses="absolute-sheet-buttons"
              metadata={availableMetadata}
              metadataToColumnSettingMapper={metadataToColumnSettingMapper}
              patchMetadataAfterSettingsUpdated={patchMetadataCallback}
              setMetadata={handleMetadataChangeCallback}
              maxRows={Math.max(
                groups.reduce((acc, group) => acc + group.max_subjects, 0),
                plannedAnimals.length
              )}
              afterChange={handleDataChange}
              afterValidation={handleValidationErrorMessage}
            />
          </div>
        )}
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="CreateAnimals"
        data-test-element="action-container"
      >
        <Button
          onClick={submitAnimals}
          testId="save-action"
          disabled={!formValid || isSubmitting}
          tooltip={formValid ? undefined : spreadSheetValidationTooltip}
        >
          {editing ? 'Save' : 'Create Animals'}
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CreateAnimals;
